<template>
  <div class="min-h-screen flex items-center justify-center px-4 py-12">
    <div class="w-full max-w-xl">
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-white mb-2">Let's Connect</h1>
        <p class="text-gray-400">
          Have a project in mind or just want to say hello? Drop me a message below.
        </p>
      </div>

      <div class="bg-gray-800/50 backdrop-blur-lg border border-gray-700/50 rounded-2xl p-6 sm:p-8 shadow-xl">
        <transition name="fade" mode="out-in">
          <form v-if="!isSubmitted" @submit.prevent="submitForm" class="space-y-5">
            <div>
              <label for="name" class="block text-sm font-medium text-gray-300 mb-1.5">Full Name</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                required
                placeholder="Enter your name"
                class="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
                @blur="validateInput('name')"
              />
              <p v-if="errors.name" class="mt-1 text-sm text-red-400">{{ errors.name }}</p>
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-300 mb-1.5">Email Address</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                required
                placeholder="Enter your email"
                class="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
                @blur="validateInput('email')"
              />
              <p v-if="errors.email" class="mt-1 text-sm text-red-400">{{ errors.email }}</p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label for="reason" class="block text-sm font-medium text-gray-300 mb-1.5">Reason for Contact</label>
                <select
                  id="reason"
                  v-model="form.reason"
                  required
                  class="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-600 text-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
                >
                  <option value="" disabled>Select a reason</option>
                  <option value="product-strategy">Product Strategy</option>
                  <option value="technical-collaboration">Technical Collaboration</option>
                  <option value="innovation-project">Innovation Project</option>
                  <option value="venture-discussion">Venture Discussion</option>
                  <option value="speaking">Speaking / Interview</option>
                  <option value="consultation">Consultation</option>
                  <option value="general">General Inquiry</option>
                </select>
              </div>

              <div>
                <label for="phone" class="block text-sm font-medium text-gray-300 mb-1.5">Phone (Optional)</label>
                <input
                  id="phone"
                  v-model="form.phone"
                  type="tel"
                  placeholder="Your phone number"
                  class="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
                />
              </div>
            </div>

            <div>
              <label for="message" class="block text-sm font-medium text-gray-300 mb-1.5">Your Message</label>
              <textarea
                id="message"
                v-model="form.message"
                required
                rows="5"
                placeholder="Type your message here..."
                class="w-full px-4 py-2.5 rounded-lg bg-gray-900/60 border border-gray-600 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors resize-y"
                @blur="validateInput('message')"
              ></textarea>
              <p v-if="errors.message" class="mt-1 text-sm text-red-400">{{ errors.message }}</p>
            </div>

            <p v-if="errors.form" class="text-sm text-red-400 text-center">{{ errors.form }}</p>

            <button
              type="submit"
              :disabled="isLoading"
              class="w-full py-3 px-6 bg-teal-500 hover:bg-teal-400 disabled:bg-gray-600 disabled:cursor-not-allowed text-gray-900 font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <span v-if="!isLoading">
                Send Message
                <i class="fas fa-paper-plane"></i>
              </span>
              <span v-else class="inline-block w-5 h-5 border-2 border-gray-900/30 border-t-gray-900 rounded-full animate-spin"></span>
            </button>
          </form>

          <div v-else class="text-center py-8">
            <div class="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 text-3xl">✓</div>
            <h2 class="text-2xl font-bold text-white mb-2">Thank You!</h2>
            <p class="text-gray-400 mb-6">Your message has been sent. I'll get back to you soon.</p>
            <button
              type="button"
              @click="resetForm"
              class="px-6 py-2.5 border border-teal-400 text-teal-400 hover:bg-teal-400/10 rounded-lg font-medium transition-colors"
            >
              Send Another Message
            </button>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref } from 'vue';
import emailjs from 'emailjs-com';

const form = reactive({
  name: '',
  email: '',
  phone: '',
  reason: '',
  message: '',
});

const errors = reactive({
  name: '',
  email: '',
  message: '',
  form: '',
});

const isSubmitted = ref(false);
const isLoading = ref(false);

const emailjsConfig = {
  serviceId: process.env.VUE_APP_EMAILJS_SERVICE_ID || 'service_dglkfy8',
  templateId: process.env.VUE_APP_EMAILJS_TEMPLATE_ID || 'template_gbwxpgd',
  publicKey: process.env.VUE_APP_EMAILJS_PUBLIC_KEY || 'Ma-s_nnSD1NmNYYXu',
};

function validateInput(field) {
  if (field === 'name') {
    errors.name = form.name.trim().length < 2 ? 'Name must be at least 2 characters' : '';
  } else if (field === 'email') {
    errors.email = !/\S+@\S+\.\S+/.test(form.email) ? 'Please enter a valid email' : '';
  } else if (field === 'message') {
    errors.message = form.message.trim().length < 10 ? 'Message must be at least 10 characters' : '';
  }
}

async function submitForm() {
  validateInput('name');
  validateInput('email');
  validateInput('message');
  errors.form = '';

  if (errors.name || errors.email || errors.message) {
    return;
  }

  isLoading.value = true;

  try {
    await emailjs.send(
      emailjsConfig.serviceId,
      emailjsConfig.templateId,
      {
        from_name: form.name,
        from_email: form.email,
        phone: form.phone,
        reason: form.reason,
        message: form.message,
      },
      emailjsConfig.publicKey
    );
    isSubmitted.value = true;
  } catch (error) {
    console.error('Failed to send message:', error);
    errors.form = 'There was an issue sending your message. Please try again or email jerome@jeromejbrown.com directly.';
  } finally {
    isLoading.value = false;
  }
}

function resetForm() {
  form.name = '';
  form.email = '';
  form.phone = '';
  form.reason = '';
  form.message = '';
  errors.name = '';
  errors.email = '';
  errors.message = '';
  errors.form = '';
  isSubmitted.value = false;
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>
